import { gql } from '@apollo/client'
import { Container, Grid } from '@material-ui/core'
import { useTheme } from '@material-ui/core/styles'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { useRouter } from 'next/router'
import React, { PropsWithChildren, useMemo } from 'react'
import { Breadcrumb } from '../../components/breadcrumbs/Breadcrumbs'
import { IMAGE_FALLBACK_FRAGMENT } from '../../fragments/image'
import { isNotNull, nonNull } from '../../utils/nonNull'
import { StyledHeading } from '../home'
import { NewsLandingQuery } from './__generated__'
import { Banner, NEWS_BANNER_FRAGMENT } from './fragments/banner'
import { NEWS_LANDING_CTA_FRAGMENT, NewsLandingCTA } from './fragments/cta'
import { NEWS_LANDING_LINKS_QUERY, NewsLandingLinks } from './fragments/links'
import { NewsFilters } from './fragments/newsFilter'
import {
  PopularGuidesAndArticlesWrapper,
  StyledBgContainer,
  StyledBottomFiller,
  StyledBreadcrumbs,
  StyledHeaderWrapper,
  StyledPageTitle,
  StyledSectionWrapper,
} from './templateStyles'

export type NewsLandingProps = NewsLandingQuery & {
    //
}

export const NewsLanding: React.FC<PropsWithChildren<NewsLandingProps>> = ({ entry, globalSet, categories, entries: baseEntries, entryCount: baseEntryCount }) => {
  if (!entry || entry.__typename !== 'newsLandingPage_newsLandingPage_Entry') {
    return null
  }

  const theme = useTheme()
  const router = useRouter()
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'))

  let imageFallbackUrl: string | undefined

  if(isNotNull(globalSet) && globalSet.__typename === 'siteSettings_GlobalSet' && isNotNull(globalSet.newsFallbackImage) && isNotNull(globalSet.newsFallbackImage[0]))
  {
    imageFallbackUrl = globalSet.newsFallbackImage?.[0]?.url??''
  }

  const breadcrumbs = useMemo(() => (
    <StyledBreadcrumbs>
      <Breadcrumb href={'/'} home text={'Home'} />
      <Breadcrumb href={router.pathname} text={'News'} />
    </StyledBreadcrumbs>
  ), [router])

  return (
    <>
      <Container maxWidth={'lg'}>
        <Grid container justifyContent={'center'}>
          <StyledHeaderWrapper item xs md={10}>
            {isMobile ? breadcrumbs : null}
            <StyledPageTitle variant="h2">{'Community'}</StyledPageTitle>
            <StyledPageTitle variant="h1">{'Credit Union SA News'}</StyledPageTitle>
            {isMobile ? null : breadcrumbs}
          </StyledHeaderWrapper>
        </Grid>
      </Container>
      {entry.newsLandingBanner?.[0]?.newsArticleSelect && (
        <Container>
          <StyledSectionWrapper direction={'column'} container>
            <Grid item component={'section'}>
              {/** @ts-ignore */}
              <Banner data={nonNull(entry.newsLandingBanner[0].newsArticleSelect)} imageFallbackUrl={imageFallbackUrl} />
            </Grid>
          </StyledSectionWrapper>
        </Container>
      )}
      <StyledBgContainer>
        <Container maxWidth={'lg'}>
          <NewsFilters entry={entry} categories={categories} entries={baseEntries} entryCount={baseEntryCount} globalSet={globalSet} />
          {entry.twoColumnCta && (
            <StyledSectionWrapper direction={'column'} alignItems={'center'} container>
              <Grid item xs={12} lg={11} component={'section'}>
                <NewsLandingCTA data={nonNull(entry.twoColumnCta)}/>
              </Grid>
            </StyledSectionWrapper>
          )}
        </Container>
      </StyledBgContainer>
      {entry.popularGuideArticles && (
        <PopularGuidesAndArticlesWrapper maxWidth={'lg'}>
          <StyledSectionWrapper direction={'column'} alignItems={'center'} container>
            <Grid item xs={12} component={'section'}>
              <StyledHeading variant="h5" align="center">
                {'POPULAR GUIDES AND ARTICLES'}
              </StyledHeading>
            </Grid>
            <Grid item xs={12} component={'section'}>
              <NewsLandingLinks data={nonNull(entry.popularGuideArticles)} />
            </Grid>
          </StyledSectionWrapper>
        </PopularGuidesAndArticlesWrapper>
      )}
      <StyledBottomFiller/>
    </>
  )
}

export const NEWS_LANDING_QUERY = gql`
  ${NEWS_BANNER_FRAGMENT}
  ${NEWS_LANDING_CTA_FRAGMENT}
  ${NEWS_LANDING_LINKS_QUERY}
  ${IMAGE_FALLBACK_FRAGMENT}

  query NewsLandingQuery ($uri: [String], $uid: [String], $category: [QueryArgument], $offset: Int = 0) {
    entry(section: "newsLandingPage", uri: $uri, uid: $uid) {
      ... on newsLandingPage_newsLandingPage_Entry {
        newsLandingBanner {
          ... on newsLandingBanner_BlockType {
            newsArticleSelect {
              ...NewsBannerFragment 
            }
          }
        }
        twoColumnCta {
            ...NewsLandingCtaFragment
        }
        popularGuideArticles {
            ...NewsLandingPopularLinks 
        }
      }
    }categories(group: "newsArticles", relatedToEntries: {section: "news"}) {
    ... on newsArticles_Category {
      id
      title
    }
  }
    entryCount(section: "news", newsCategory: $category)
    entries(section: "news", relatedToCategories: {id: $category}, offset: $offset, limit: 12) {
        ... on news_newsEntryType_Entry {
            id
            title
            postDate
            newsCategory {
              id
              title
            }
            newsDetailBanner {
              ... on newsDetailBanner_BlockType {
                id
                bannerImage {
                  url
                }
              }
            }
            uri
        }
    }
    globalSet {
      ...ImageFallbackFragment
    }
  }
`